import { useMemo } from 'react';
import type { AccountType } from '@/components/Modals/CreateAccount/types';
import { type FallbackPlanKey, getFallbackPrice } from '@/data/pricing-fallbacks';
import { usePricingOptions } from '@/hooks/api/usePricingOptions';
import { useSettings } from '@/hooks/state/useSettings';
import { isBusinessPlanName } from '@/lib/pricing/businessPlans';
import type { PricingOption } from '@/hooks/api/usePricingOptions';
interface UseNewPricingProps {
  accountType?: AccountType;
}
const useNewPricing = ({
  accountType
}: UseNewPricingProps = {}) => {
  const {
    data: pricingOptions,
    isLoading,
    error
  } = usePricingOptions();
  const {
    settings
  } = useSettings();
  const currencyCode = settings?.currency.code?.toLowerCase() ?? 'gbp';
  const pricing = useMemo(() => {
    if (!pricingOptions) {
      return null;
    }

    // Find the pricing options based on the new naming
    const freePlan = pricingOptions.find((option: PricingOption) => option.title.toLowerCase() === 'free');
    const essentialsPlan = pricingOptions.find((option: PricingOption) => option.title.toLowerCase() === 'essentials');
    const creatorPlan = pricingOptions.find((option: PricingOption) => option.title.toLowerCase() === 'creator');
    const proPlan = pricingOptions.find((option: PricingOption) => option.title.toLowerCase() === 'pro');

    // The 2026 top tier — NOT `proPlan` above, which carried the Business
    // name before 2025.
    const businessPlan = pricingOptions.find((option: PricingOption) => isBusinessPlanName(option.title));

    // Fallbacks for when plans aren't available from the server
    if (!(creatorPlan || proPlan)) {
      console.warn('Pricing data not available from the server');
      return null;
    }

    // Helper function to format prices
    const formatPrice = (price: number | undefined) => {
      if (!price) {
        return {
          integer: '0',
          decimals: '.00',
          full: '0.00',
          pointIndex: 1
        };
      }
      const priceString = price.toFixed(2);
      const pointIndex = priceString.indexOf('.');
      return {
        full: priceString,
        integer: priceString.slice(0, pointIndex),
        decimals: priceString.slice(pointIndex),
        pointIndex
      };
    };

    // Get currency-specific pricing with a fallback to the Phase 1 winner
    // baseline. The API doesn't yet return INR/BRL/AUD prices (DEV-5672), so
    // without this fallback newEssentials/newCreator/newPro arrive zero-priced
    // for those currencies and Stripe `<Elements>` rejects amount=0.
    const getCurrencyPrice = (plan: PricingOption | undefined, field: 'monthly' | 'yearly', fallbackPlan: FallbackPlanKey): number | undefined => {
      const apiValue = plan?.pricing[currencyCode as keyof typeof plan.pricing]?.[field];
      if (apiValue && apiValue > 0) {
        return apiValue;
      }
      return getFallbackPrice(fallbackPlan, currencyCode, field);
    };

    // Get prices with proper fallbacks
    const creatorMonthlyPrice = getCurrencyPrice(creatorPlan, 'monthly', 'creator');
    const creatorYearlyPrice = getCurrencyPrice(creatorPlan, 'yearly', 'creator');
    const creatorYearlyMonthlyPrice = creatorYearlyPrice ? creatorYearlyPrice / 12 : 0;
    const proMonthlyPrice = getCurrencyPrice(proPlan, 'monthly', 'pro');
    const proYearlyPrice = getCurrencyPrice(proPlan, 'yearly', 'pro');
    const proYearlyMonthlyPrice = proYearlyPrice ? proYearlyPrice / 12 : 0;
    const essentialsMonthlyPrice = getCurrencyPrice(essentialsPlan, 'monthly', 'essentials');
    const essentialsYearlyPrice = getCurrencyPrice(essentialsPlan, 'yearly', 'essentials');
    const essentialsYearlyMonthlyPrice = essentialsYearlyPrice ? essentialsYearlyPrice / 12 : 0;

    // Format all prices
    const creatorMonthlyFormatted = formatPrice(creatorMonthlyPrice);
    const creatorYearlyFormatted = formatPrice(creatorYearlyPrice);
    const creatorYearlyMonthlyFormatted = formatPrice(creatorYearlyMonthlyPrice);
    const proMonthlyFormatted = formatPrice(proMonthlyPrice);
    const proYearlyFormatted = formatPrice(proYearlyPrice);
    const proYearlyMonthlyFormatted = formatPrice(proYearlyMonthlyPrice);
    const businessMonthlyFormatted = formatPrice(getCurrencyPrice(businessPlan, 'monthly', 'business'));
    const businessYearlyPrice = getCurrencyPrice(businessPlan, 'yearly', 'business');
    const businessYearlyFormatted = formatPrice(businessYearlyPrice);
    const businessYearlyMonthlyFormatted = formatPrice(businessYearlyPrice ? businessYearlyPrice / 12 : 0);
    const essentialsMonthlyFormatted = formatPrice(essentialsMonthlyPrice);
    const essentialsYearlyFormatted = formatPrice(essentialsYearlyPrice);
    const essentialsYearlyMonthlyFormatted = formatPrice(essentialsYearlyMonthlyPrice);
    return {
      period: 'yearly',
      currency: settings?.currency,
      // Map the Creator plan to Premium for backward compatibility
      premium: creatorPlan ? {
        id: creatorPlan.subscription_stripe_plan_id,
        title: creatorPlan.title,
        description: creatorPlan.description,
        benefits: creatorPlan.benefits,
        is_popular: creatorPlan.is_popular,
        monthly: {
          monthlyPrice: creatorMonthlyFormatted.full,
          monthlyPointIndex: creatorMonthlyFormatted.pointIndex,
          monthlyInteger: creatorMonthlyFormatted.integer,
          monthlyDecimals: creatorMonthlyFormatted.decimals
        },
        yearly: {
          yearlyPrice: creatorYearlyFormatted.full,
          yearlyPointIndex: creatorYearlyFormatted.pointIndex,
          yearlyInteger: creatorYearlyFormatted.integer,
          yearlyDecimals: creatorYearlyFormatted.decimals,
          monthlyEquivalent: {
            monthlyEquivalentPrice: creatorYearlyMonthlyFormatted.full,
            monthlyEquivalentPointIndex: creatorYearlyMonthlyFormatted.pointIndex,
            monthlyEquivalentInteger: creatorYearlyMonthlyFormatted.integer,
            monthlyEquivalentDecimals: creatorYearlyMonthlyFormatted.decimals
          }
        }
      } : null,
      // The Pro plan. Named `business` until 2026 because Business was Pro's
      // pre-2025 name — renamed to `pro` so it can't be read as the 2026 tier.
      pro: proPlan ? {
        id: proPlan.subscription_stripe_plan_id,
        title: proPlan.title,
        description: proPlan.description,
        benefits: proPlan.benefits,
        is_popular: proPlan.is_popular,
        monthly: {
          monthlyPrice: proMonthlyFormatted.full,
          monthlyPointIndex: proMonthlyFormatted.pointIndex,
          monthlyInteger: proMonthlyFormatted.integer,
          monthlyDecimals: proMonthlyFormatted.decimals
        },
        yearly: {
          yearlyPrice: proYearlyFormatted.full,
          yearlyPointIndex: proYearlyFormatted.pointIndex,
          yearlyInteger: proYearlyFormatted.integer,
          yearlyDecimals: proYearlyFormatted.decimals,
          monthlyEquivalent: {
            monthlyEquivalentPrice: proYearlyMonthlyFormatted.full,
            monthlyEquivalentPointIndex: proYearlyMonthlyFormatted.pointIndex,
            monthlyEquivalentInteger: proYearlyMonthlyFormatted.integer,
            monthlyEquivalentDecimals: proYearlyMonthlyFormatted.decimals
          }
        }
      } : null,
      // Add the new Essentials plan
      essentials: essentialsPlan ? {
        id: essentialsPlan.subscription_stripe_plan_id,
        title: essentialsPlan.title,
        description: essentialsPlan.description,
        benefits: essentialsPlan.benefits,
        is_popular: essentialsPlan.is_popular,
        monthly: {
          monthlyPrice: essentialsMonthlyFormatted.full,
          monthlyPointIndex: essentialsMonthlyFormatted.pointIndex,
          monthlyInteger: essentialsMonthlyFormatted.integer,
          monthlyDecimals: essentialsMonthlyFormatted.decimals
        },
        yearly: {
          yearlyPrice: essentialsYearlyFormatted.full,
          yearlyPointIndex: essentialsYearlyFormatted.pointIndex,
          yearlyInteger: essentialsYearlyFormatted.integer,
          yearlyDecimals: essentialsYearlyFormatted.decimals,
          monthlyEquivalent: {
            monthlyEquivalentPrice: essentialsYearlyMonthlyFormatted.full,
            monthlyEquivalentPointIndex: essentialsYearlyMonthlyFormatted.pointIndex,
            monthlyEquivalentInteger: essentialsYearlyMonthlyFormatted.integer,
            monthlyEquivalentDecimals: essentialsYearlyMonthlyFormatted.decimals
          }
        }
      } : null,
      // The 2026 Business tier — a different plan from `pro` above, despite
      // once sharing its name.
      business: businessPlan ? {
        id: businessPlan.subscription_stripe_plan_id,
        title: businessPlan.title,
        description: businessPlan.description,
        benefits: businessPlan.benefits,
        is_popular: businessPlan.is_popular,
        monthly: {
          monthlyPrice: businessMonthlyFormatted.full,
          monthlyPointIndex: businessMonthlyFormatted.pointIndex,
          monthlyInteger: businessMonthlyFormatted.integer,
          monthlyDecimals: businessMonthlyFormatted.decimals
        },
        yearly: {
          yearlyPrice: businessYearlyFormatted.full,
          yearlyPointIndex: businessYearlyFormatted.pointIndex,
          yearlyInteger: businessYearlyFormatted.integer,
          yearlyDecimals: businessYearlyFormatted.decimals,
          monthlyEquivalent: {
            monthlyEquivalentPrice: businessYearlyMonthlyFormatted.full,
            monthlyEquivalentPointIndex: businessYearlyMonthlyFormatted.pointIndex,
            monthlyEquivalentInteger: businessYearlyMonthlyFormatted.integer,
            monthlyEquivalentDecimals: businessYearlyMonthlyFormatted.decimals
          }
        }
      } : null,
      // Include the free plan data
      free: freePlan ? {
        id: freePlan.subscription_stripe_plan_id,
        title: freePlan.title,
        description: freePlan.description,
        benefits: freePlan.benefits,
        is_popular: freePlan.is_popular
      } : null
    };
  }, [pricingOptions, currencyCode, settings?.currency]);
  return {
    ...pricing,
    isLoading,
    error
  };
};
export default useNewPricing;